/**
 * View controls the screen makes selectable, and the per-query knobs a choice maps to. A choice never
 * edits the saved report: it re-runs this query only. Pure + React-free.
 */

import type { ViewControl } from 'src/components/star-grid/api/reportEngineTypes';
import { screenChoices, type ChoiceOption } from 'src/components/star-grid/utils/screenOptions';

/** Which `QueryRequest` field a control's choice travels in. */
export type ViewControlRoute = 'as_of' | 'deck_type' | 'view_overrides';

/** Version and Deck have dedicated request fields; every other control is a keyed view override. */
const DEDICATED_ROUTES: Readonly<Record<string, ViewControlRoute>> = { version: 'as_of', deck: 'deck_type' };

const viewControlRoute = (controlId: string): ViewControlRoute => DEDICATED_ROUTES[controlId] ?? 'view_overrides';

/** The dimension every Version pick reads: the report-wide `version` and a comparison's `Version1`/`Version2`. */
export const VERSION_DIMENSION = 'FPA Deck Version';

export const isVersionControl = (control: ViewControl): boolean => control.id === 'version' || control.dimension === VERSION_DIMENSION;

export interface SelectableViewControl {
  readonly control: ViewControl;
  readonly options: readonly ChoiceOption[];
  /** The option the saved report shows; choosing it again clears the override. */
  readonly defaultValue: string;
}

/** The control's own value as an option id: the deck may state either the id or the label. */
const defaultValueOf = (control: ViewControl, options: readonly ChoiceOption[]): string =>
  options.find((option) => option.value === control.value)?.value ?? options.find((option) => option.label === control.value)?.value ?? control.value;

/** Controls the screen states as selectable: not read-only and offering a real choice. */
export const selectableViewControls = (controls: readonly ViewControl[]): SelectableViewControl[] =>
  controls.flatMap((control) => {
    if (control.read_only) return [];
    const options = screenChoices(control.options);
    return options.length > 1 ? [{ control, options, defaultValue: defaultValueOf(control, options) }] : [];
  });

export interface ScreenControlKnobs {
  readonly asOf?: string;
  readonly deckType?: string;
  readonly viewOverrides: Readonly<Record<string, string>>;
}

/** The knobs for the session's choices; a choice equal to the report's own value sends nothing. */
export const screenControlKnobs = (selectable: readonly SelectableViewControl[], chosen: Readonly<Record<string, string>>): ScreenControlKnobs => {
  const knobs: { asOf?: string; deckType?: string; viewOverrides: Record<string, string> } = { viewOverrides: {} };
  for (const { control, options, defaultValue } of selectable) {
    const value = chosen[control.id];
    if (value === undefined || value === defaultValue || !options.some((option) => option.value === value)) continue;
    const route = viewControlRoute(control.id);
    if (route === 'as_of') knobs.asOf = value;
    else if (route === 'deck_type') knobs.deckType = value;
    else knobs.viewOverrides[control.id] = value;
  }
  return knobs;
};
